/**
 * Whether the GPU can be timed on a given renderer — and when it cannot, WHY.
 *
 * "Not supported" is the wrong answer to give a user, because the three real
 * cases have three different fixes (or none):
 *
 * - **WebGL** needs `EXT_disjoint_timer_query_webgl2`. The browser/GPU decides;
 *   Chrome hides it behind a flag on some setups.
 * - **WebGPU** writes timestamps per RENDER PASS, which needs the
 *   `timestamp-query` feature on the GPUDevice. three requests every feature the
 *   adapter supports (WebGPUBackend.js:227-246), so a three-created device has
 *   it whenever the hardware does — but a page that built its OWN device may not,
 *   and a feature cannot be added to a device after the fact.
 * - **WebGPU with the feature but tracking off** looks fixable — flipping
 *   `backend.trackTimestamp` does start the pool — but WE MUST NOT. three stores
 *   the pool's querySet on the CACHED render-pass descriptors
 *   (WebGPUBackend.js:2270) and never re-validates it, so once the page's own
 *   lifecycle disposes that pool every later submit references a destroyed
 *   QuerySet: "[QuerySet ...] used while destroyed", forever, thousands of times
 *   (observed on threejs.paris, 2026-08-14). Timestamps have to be the app's
 *   decision — `new WebGPURenderer({ trackTimestamp: true })`.
 */

export type TimingBackend = "webgl" | "webgpu" | "unknown";

export interface TimingSupport {
    readonly backend: TimingBackend;
    readonly supported: boolean;
    /** WebGPU: tracking is off but the device could do it — flip it to profile. */
    readonly needsEnable?: boolean;
    /** why not, in words a user can act on (or stop acting on) */
    readonly reason?: string;
}

interface RendererLike {
    isWebGPURenderer?: boolean;
    isWebGLRenderer?: boolean;
    backend?: {
        trackTimestamp?: boolean;
        device?: { features?: { has?(name: string): boolean } };
        /** WebGL fallback backend: the EXT_disjoint_timer_query extension it found */
        disjoint?: unknown;
    };
    getContext?(): { getExtension?(name: string): unknown } | null;
}

/** The WebGPU device feature that makes per-pass timestamps possible. */
export const TIMESTAMP_FEATURE = "timestamp-query";

export function describeTimingSupport(renderer: unknown): TimingSupport {
    const r = renderer as RendererLike | null | undefined;

    // WebGPU first: a WebGPURenderer also answers getContext(), but with a
    // GPUCanvasContext that has no getExtension — testing WebGL first would throw
    // (it did: the tool used to die on WebGPU pages instead of declining).
    if (r?.isWebGPURenderer === true || r?.backend) {
        const device = r?.backend?.device;
        // A WebGPURenderer may be running on the WEBGL FALLBACK backend, which gates
        // timestamps on EXT_disjoint_timer_query (`disjoint`) rather than a device
        // feature — checking only the device would report a missing feature that
        // never applied there.
        const hasFeature = device?.features?.has?.(TIMESTAMP_FEATURE) === true || !!r?.backend?.disjoint;
        if (!hasFeature) {
            return {
                backend: "webgpu",
                supported: false,
                reason: `the GPUDevice was created without the "${TIMESTAMP_FEATURE}" feature`
                    + ` — a device cannot gain a feature after creation, so this page cannot be GPU-timed.`
                    + ` (three requests it automatically; a page that supplies its own device must ask for it.)`,
            };
        }
        if (r?.backend?.trackTimestamp !== true) {
            return {
                backend: "webgpu",
                supported: false,
                // WHY we don't just switch it on is in the file header — the user
                // needs the fix, not the reasoning
                reason: "GPU timestamps are off on this renderer. Create it with { trackTimestamp: true } to measure.",
            };
        }
        return { backend: "webgpu", supported: true, needsEnable: false };
    }

    if (r?.isWebGLRenderer === true || typeof r?.getContext === "function") {
        let ext: unknown = null;
        try {
            const gl = r.getContext?.();
            ext = gl?.getExtension?.("EXT_disjoint_timer_query_webgl2") ?? gl?.getExtension?.("EXT_disjoint_timer_query") ?? null;
        }
        catch { /* not a WebGL context after all */ }
        if (!ext) {
            return {
                backend: "webgl",
                supported: false,
                reason: "EXT_disjoint_timer_query is not available on this browser/GPU."
                    + " Chrome exposes it with --enable-webgl-developer-extensions.",
            };
        }
        return { backend: "webgl", supported: true };
    }

    return { backend: "unknown", supported: false, reason: "no three.js WebGL or WebGPU renderer was found." };
}

/**
 * The frame rate the GPU alone would allow, from a per-frame millisecond figure.
 *
 * A GPU number is unreadable next to an fps number until one is expressed as the
 * other — "1.82 ms" beside "46 fps" looks like a contradiction, and is in fact
 * the useful signal: the frame is not GPU-bound. Undefined when there is nothing
 * to convert, never Infinity.
 */
export function gpuBoundFps(gpuMs: number): number | undefined {
    if (!Number.isFinite(gpuMs) || gpuMs <= 0) return undefined;
    return Math.round(1000 / gpuMs);
}

/**
 * What percentage of the frame the user is ACTUALLY getting was GPU work — the
 * one-line verdict on which side is the limit. Undefined without a frame rate to
 * measure against.
 */
export function gpuShareOfFrame(gpuMs: number, fps: number | undefined): number | undefined {
    if (!Number.isFinite(gpuMs) || gpuMs <= 0) return undefined;
    if (!fps || !Number.isFinite(fps) || fps <= 0) return undefined;
    return Math.round((gpuMs / (1000 / fps)) * 100);
}
